import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import Input from 'components/common/Input';
import { useForm } from 'react-hook-form';
import Flex from 'components/common/Flex';
import Avatar from 'components/common/Avatar';
import Socials from './Socials';
import { checkDomain, validateURL } from 'helpers/utils';
import IconButton from 'components/common/IconButton';
import corner4 from 'assets/img/illustrations/corner-2.png';
import SoftBadge from 'components/common/SoftBadge';
import Background from 'components/common/Background';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import UploadFiles from 'components/common/UploadFiles';
const img =
 'data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22857%22%20height%3D%22180%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20857%20180%22%20preserveAspectRatio%3D%22none%22%3E%3Cdefs%3E%3Cstyle%20type%3D%22text%2Fcss%22%3E%23holder_1896c52ddc1%20text%20%7B%20fill%3A%23999%3Bfont-weight%3Anormal%3Bfont-family%3AArial%2C%20Helvetica%2C%20Open%20Sans%2C%20sans-serif%2C%20monospace%3Bfont-size%3A43pt%20%7D%20%3C%2Fstyle%3E%3C%2Fdefs%3E%3Cg%20id%3D%22holder_1896c52ddc1%22%3E%3Crect%20width%3D%22857%22%20height%3D%22180%22%20fill%3D%22%23373940%22%3E%3C%2Frect%3E%3Cg%3E%3Ctext%20x%3D%22318.5124969482422%22%20y%3D%22109.2%22%3E857x180%3C%2Ftext%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E';

const CompanyForm = ({ data, onSubmitData, onError, isLoading, className }) => {
 const [banner, setBanner] = useState(data?.banner ?? img);
 const {
  watch,
  register,
  setValue,
  handleSubmit,
  formState: { errors }
 } = useForm({ defaultValues: { socials: [], logo: [], banner: null, ...data } });

 const checkdomain = async value => {
  const urlOrigin = await checkDomain(window.location.hostname);
  if (value.domain) {
   const url = await checkDomain(value.domain);
   if (urlOrigin.domain === url.domain) return setValue('domain', [value.subdomain.toLowerCase(), url.domain].join('.'));
  } else return setValue('domain', [value.subdomain.toLowerCase(), urlOrigin.domain].join('.'));
 };

 useEffect(() => {
  const subscription = watch((value, { name }) => {
   if (name === 'subdomain') checkdomain(value);
   //    console.log(value);
  });
  return () => subscription.unsubscribe();
 }, [watch]);

 return (
  <Card className={className}>
   <div className="position-relative">
    <Card.Img
     variant="top"
     src={banner}
     style={{ minHeight: '100px', maxHeight: '180px' }}
     //src="https://www.anaisindia.com/wp-content/uploads/2019/01/fotografia-panoramica.jpg"
    />
    <UploadFiles
     preview={false}
     className="mb-2"
     multiple={false}
     accept="image/*"
     dropzoneArea={false}
     style={{ maxHeight: 300 }}
     onChange={e => {
      setValue('banner', !e?.length ? null : e);
      setBanner(e?.length ? e[0]?.base64 : img);
     }}
     placeholder={
      <Button
       variant="falcon-primary"
       type="button"
       className="p-0 m-3 icon-item icon-item-sm position-absolute top-0 end-0 z-index-1"
      >
       <FontAwesomeIcon icon="pencil-alt" />
      </Button>
     }
    />

    {watch('banner') && (
     <Button
      variant="outline-danger"
      size="sm"
      className="px-2 p-auto mx-3 mt-6 position-absolute top-0 end-0 z-index-1 rounded-circle"
      onClick={() => {
       setValue('banner', null);
       setBanner(img);
      }}
     >
      <FontAwesomeIcon icon="trash" />
     </Button>
    )}

    {data?.verified && (
     <SoftBadge pill bg="success" className="fs--2 me-3 mb-4 position-absolute bottom-0 end-0 z-index-1">
      <FontAwesomeIcon icon="medal" className="me-1" />
      Verificada
     </SoftBadge>
    )}

    <Flex
     wrap="no-wrap"
     justifyContent="center"
     alignItems="center"
     className="px-3 w-100 position-absolute top-100 start-0 translate-middle-y z-index-1"
    >
     <Avatar
      uploader
      size="4xl"
      src={watch('logo')}
      mediaClass="img-thumbnail shadow-sm"
      propUploader={{
       iconClass: 'fs-0',
       classInput: 'logo5',
       register: register('logo', { required: 'El logo es requerido' }),
       onChange: img => setValue('logo', img, { shouldValidate: true, shouldTouch: true }),
       multiple: false
      }}
     />
     <div className="flex-fill border-sm-start border-start-2 ps-sm-3 ms-sm-3 ps-2">
      <Input
       type="text"
       label="Alias de empresa*"
       name="subdomain"
       errors={errors}
       placeholder="Subdominio de la Empresa"
       formLabelProps={{ className: 'mb-0 fs--1 text-white' }}
       formGroupProps={{ className: 'mb-2' }}
       formControlProps={{
        ...register('subdomain', { required: true, validate: value => !/[^a-zA-Z0-9_-ñÑ]/g.test(value) || false }),
        autoComplete: 'off',
        size: 'sm'
       }}
      />

      <Socials watch={watch} errors={errors} setValue={setValue} smHiden={true} />
      <Input
       type="hidden"
       formControlProps={{ ...register('socials', { validate: value => !value.some(e => e.url && !validateURL(e.url)) }) }}
      />
     </div>
    </Flex>
   </div>
   <Card.Body className="fs--1">
    <Background
     image={corner4}
     className="bg-card"
     style={{
      borderTopRightRadius: '0.375rem',
      borderBottomRightRadius: '0.375rem'
     }}
    />
    <Form onSubmit={handleSubmit(onSubmitData, onError)} className="position-relative mt-4">
     <Row className="my-2 g-2">
      <Input
       type="text"
       label="Nombre*"
       name="name"
       errors={errors}
       placeholder="Nombre de la Empresa"
       formLabelProps={{ className: 'mb-0 fs--1' }}
       formGroupProps={{ as: Col, lg: 8 }}
       formControlProps={{ ...register('name', { required: true }), size: 'sm', readOnly: false }}
      />
      <Input
       type="text"
       label="Nit*"
       name="nit"
       errors={errors}
       placeholder="Nit de la Empresa"
       formLabelProps={{ className: 'mb-0 fs--1' }}
       formGroupProps={{ as: Col, lg: 4 }}
       formControlProps={{ ...register('nit', { required: true }), size: 'sm', readOnly: false }}
      />
     </Row>

     <Row className="mb-2 g-2">
      <Input
       label="Movil*"
       type="tel"
       name="phone"
       errors={errors}
       placeholder="Numero cel"
       formLabelProps={{ className: 'mb-0 fs--1' }}
       formGroupProps={{ as: Col, sm: 4 }}
       formControlProps={{
        size: 'sm',
        type: 'tel',
        autoComplete: 'tel',
        value: watch('phone'),
        onChange: e => setValue('phone', e)
       }}
      />
      <Form.Control
       type="hidden"
       {...register('phone', {
        required: true,
        minLength: { value: 10, message: 'Movil invalido' },
        pattern: { value: /[+0-9]/i, message: 'Solo numeros' }
       })}
      />

      <Input
       type="text"
       label="Email*"
       name="mail"
       errors={errors}
       placeholder="Email de la Empresa"
       formLabelProps={{ className: 'mb-0 fs--1' }}
       formGroupProps={{ as: Col, lg: 5 }}
       formControlProps={{
        size: 'sm',
        className: 'input-spin-none',
        ...register('mail', {
         required: true,
         pattern: {
          value: /[A-Za-z0-9._%+-]{3,}@[a-zA-Z]{3,}([.]{1}[a-zA-Z]{2,}|[.]{1}[a-zA-Z]{2,}[.]{1}[a-zA-Z]{2,})/i,
          message: 'Email invalido'
         }
        })
       }}
      />

      <Input
       type="select"
       label="Sector*"
       name="sector"
       placeholder="Tipo de sector"
       errors={errors}
       formLabelProps={{ className: 'mb-0 fs--1' }}
       formGroupProps={{ as: Col, lg: 3 }}
       formControlProps={{ ...register('sector', { required: true }), size: 'sm', disabled: false }}
       options={[
        { label: 'Inmobiliario', value: 'REAL ESTATE' },
        { label: 'Markets', value: 'MARKETS' },
        { label: 'Transportes', value: 'TRIPS' }
       ]}
      />
     </Row>

     <Row className="mb-2 g-2">
      <Input
       type="text"
       label="Dominio"
       name="domain"
       errors={errors}
       placeholder="Sitio Web de la Empresa"
       formLabelProps={{ className: 'mb-0 fs--1' }}
       formGroupProps={{ as: Col, lg: 4 }}
       formControlProps={{ ...register('domain', { validate: v => validateURL(v) || !v }), size: 'sm' }}
      />
      <Input
       type="text"
       label="Direccion*"
       name="address"
       errors={errors}
       placeholder="Dirección de la Empresa"
       formLabelProps={{ className: 'mb-0 fs--1' }}
       formGroupProps={{ as: Col, lg: 8 }}
       formControlProps={{ ...register('address', { required: true }), size: 'sm' }}
      />
     </Row>

     <Input
      type="textarea"
      label="Descripción"
      name="description"
      errors={errors}
      placeholder="Descripción de la Empresa"
      formLabelProps={{ className: 'mb-0 fs--1' }}
      formGroupProps={{ as: Col, lg: 12 }}
      formControlProps={{ ...register('description', { minLength: 10 }), size: 'sm', rows: 2 }}
     />
     {isLoading && <Spinner animation="border" variant="primary" className="float-start" />}
     <IconButton
      type="submit"
      icon={data ? 'sync-alt' : 'plus'}
      className="mt-3 fs--1 float-end"
      transform="shrink-2"
      size="sm"
      variant="falcon-default"
     >
      {data ? 'Actualizar' : 'Crear Empresa'}
     </IconButton>
    </Form>
   </Card.Body>
  </Card>
 );
};

CompanyForm.propTypes = {
 data: PropTypes.object,
 isLoading: PropTypes.bool,
 onSubmitData: PropTypes.func.isRequired,
 onError: PropTypes.func.isRequired,
 className: PropTypes.string
};

export default CompanyForm;
